import { MinusCircleOutlined } from '@ant-design/icons';
import {
  Checkbox,
  ConfigProvider,
  Form,
  message,
  Tag,
  Typography,
  Input
} from 'antd';
import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import IKeywordsEntity from '../domain/IKeywordsEntity';
import CardContainer from '../../../components/CardContainer';
import { observer } from 'mobx-react-lite';
import StepperFooter from '../../../components/StepperFooter';
import CategoryKeywordModel from '../models/CategoryKeywordModel';
import AntdCustomCheckbox from '../../../components/buttons/AntdCustomCheckbox';
import { BodyText, SmallText } from '../../../components/ui/Typography';
import { validateTagOrKeyword } from '../../../helpers/ValidateTags';
import HelpBoxCompanionModel from '../../../models/HelpBoxCompanionModel';
import { AnimatePresence, motion } from 'framer-motion';
import {
  fadeInUpVariants,
  staggerContainerVariants
} from '../../../components/animations/StepAnimations';

const { Title, Text } = Typography;

interface FCProps {
  stepData: {
    stepTitle: string;
    subTitle: string;
    keywordEntity: IKeywordsEntity;
  };
}

const KeywordsStep: React.FC<FCProps> = observer(({ stepData }) => {
  const { t, i18n } = useTranslation();
  const keywords = stepData.keywordEntity.getKeywords();
  const [isStepLoading, setIsStepLoading] = React.useState(false);
  const [customInputValue, setCustomInputValue] = React.useState('');
  const [inputError, setInputError] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [isVisible, setIsVisible] = React.useState(true);
  const [form] = Form.useForm();

  let title: React.ReactNode = '';
  const entityType = stepData.keywordEntity.getEntityType();

  if (entityType === 'MainCategoryEntity' || entityType === 'AreaEntity') {
    title = (
      <>
        {t('Services and products for')}{' '}
        <b>"{stepData.keywordEntity.displayName}"</b>
      </>
    );
  }
  if (entityType === 'ActivityEntity') {
    title = (
      <>
        {t('Services and products for activity')}{' '}
        <b>"{stepData.keywordEntity.displayName}"</b>
      </>
    );
  }

  let allAddedKeywords = stepData.keywordEntity.getAllAddedKeywords();
  let disableAllUncheckedKeywords = allAddedKeywords.length === 5;

  const handleAddCustomKeyword = () => {
    const trimmedValue = customInputValue.trim();

    if (!trimmedValue) {
      setInputError(false);
      setErrorMessage('');
      return;
    }

    if (!validateTagOrKeyword(trimmedValue)) {
      setInputError(true);
      setErrorMessage(
        t(
          'The value must be between 2 and 100 characters long and can only contain letters (A-Z), numbers (0-9), dashes ( - ), and slashes ( / ). Special symbols are not allowed.'
        )
      );
      return;
    }

    if (
      allAddedKeywords.some(
        (keyword) => keyword.toLowerCase() === trimmedValue.toLowerCase()
      )
    ) {
      setInputError(true);
      setErrorMessage(t('This service/product already exists'));
      setCustomInputValue('');
      return;
    }

    if (
      keywords.some(
        (keyword) => keyword.toLowerCase() === trimmedValue.toLowerCase()
      )
    ) {

      const existingKeyword = keywords.find(
        (keyword) => keyword.toLowerCase() === trimmedValue.toLowerCase()
      );
      
      if (existingKeyword) {
        stepData.keywordEntity.setKeywordChecked(existingKeyword, true);
        setCustomInputValue('');
        setInputError(false);
        setErrorMessage('');
        return;
      }
    }

    if (!validateTagOrKeyword(trimmedValue)) {
      setInputError(true);
      setErrorMessage(
        t(
          'The value must be between 2 and 100 characters long and can only contain letters (A-Z), numbers (0-9), dashes ( - ), and slashes ( / ). Special symbols are not allowed.'
        )
      );
      return;
    }

    setInputError(false);
    setErrorMessage('');
    stepData.keywordEntity.setKeywordChecked(trimmedValue, true);
    setCustomInputValue('');
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCustomKeyword();
    }
  };

  const helpBoxTitleMessage = t('Why is that important?'); 
  const helpBoxParagraphMessage = t('The selection of products and services is used to create a website created optimally for the search engines. The products and services serve as the basis for the pages to be created and the texts on it.');
  
  React.useEffect(() => {
    HelpBoxCompanionModel.setTitleMessage(helpBoxTitleMessage);
    HelpBoxCompanionModel.setParagraphMessage(helpBoxParagraphMessage);
  }, [i18n.language]);  

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={staggerContainerVariants}
        >
          <CardContainer title={title}>
            <div className="pt-2 mb-7 text-left sm:text-center">
              <motion.div variants={fadeInUpVariants} className="mb-[48px]">
                <BodyText className="mb-2">
                  <Trans
                    i18nKey="keywordsStep.description"
                    defaults="Help us to determine the right <b>services and products</b> which best suit. <br> Define <b>1-5 services or products</b> for this category."
                    components={{ b: <b /> }}
                  />
                </BodyText>
              </motion.div>

              <div className="flex flex-col gap-5">
                <motion.div 
                  variants={fadeInUpVariants}
                  className="flex flex-wrap gap-4 justify-start sm:justify-center"
                >
                  {keywords.map((keyword, index) => (
                    <div key={`${keyword}-${index}`}>
                      <AntdCustomCheckbox
                        appearance="basic"
                        disabled={
                          disableAllUncheckedKeywords &&
                          !stepData.keywordEntity.isKeywordChecked(keyword)
                        }
                        checked={stepData.keywordEntity.isKeywordChecked(keyword)}
                        onChange={(e) => {
                          stepData.keywordEntity.setKeywordChecked(
                            keyword,
                            e.target.checked
                          );
                        }}>
                        <Typography.Text>{keyword}</Typography.Text>
                      </AntdCustomCheckbox>
                    </div>
                  ))}
                </motion.div>

                <motion.div 
                  variants={fadeInUpVariants} 
                  className="py-2 flex flex-col gap-2 text-left"
                >
                  <Typography.Text className="text-left px-[18px]" strong>
                    {t('Complete your own services and products')}
                  </Typography.Text>
                  <div
                    className="flex flex-wrap gap-2 px-[16px] py-[8px] rounded-full shadow-sm justify-start sm:justify-center min-h-[48px] items-center"
                    style={{
                      backgroundColor:
                        allAddedKeywords.length < 5
                          ? 'rgba(238, 251, 255, 1)'
                          : 'white',
                      border: `1px solid ${
                        allAddedKeywords.length < 5
                          ? 'rgba(183, 235, 255, 1)'
                          : 'rgba(0, 0, 0, 0.15)'
                      }`
                    }}
                    onClick={() => {
                      const input = document.getElementById(
                        'custom-keyword-input'
                      ) as HTMLInputElement;
                      if (input) input.focus();
                    }}>
                    {allAddedKeywords
                      .filter((keyword) => !keywords.includes(keyword))
                      .map((keyword, idx) => (
                        <Tag
                          key={`${keyword}-${idx}`}
                          icon={
                            <MinusCircleOutlined
                              onClick={() =>
                                stepData.keywordEntity.setKeywordChecked(
                                  keyword,
                                  false
                                )
                              }
                            />
                          }
                          color="default"
                          style={{
                            fontSize: 12,
                            padding: '1px 8px',
                            borderRadius: '50px',
                            backgroundColor: '#ffffff',
                            border: '1px solid rgba(0, 0, 0, 0.15)'
                          }}>
                          {keyword}
                        </Tag>
                      ))}

                    <Input
                      id="custom-keyword-input"
                      value={customInputValue}
                      onChange={(e) => {
                        setCustomInputValue(e.target.value);
                        setInputError(false);
                        setErrorMessage('');
                      }}
                      onKeyPress={handleKeyPress}
                      onBlur={handleAddCustomKeyword}
                      disabled={allAddedKeywords.length >= 5}
                      style={{
                        border: 'none',
                        outline: 'none',
                        boxShadow: 'none',
                        background: 'transparent',
                        padding: 0,
                        fontSize: 12,
                        flex: 1,
                        minWidth:
                          allAddedKeywords.filter(
                            (keyword) => !keywords.includes(keyword)
                          ).length === 0
                            ? '200px'
                            : '100px'
                      }}
                      className="custom-keyword-input"
                    />
                  </div>
                  {inputError && (
                    <SmallText className="px-[18px]" style={{ color: '#ff4d4f' }}>
                      {errorMessage}
                    </SmallText>
                  )}
                  <SmallText className="px-[16px]">
                    {t('Press [Enter] to add your own services or products')}
                  </SmallText>
                </motion.div>
              </div>
            </div>
            
            <motion.div variants={fadeInUpVariants}>
              <StepperFooter
                buttonId="category-save-next"
                buttonGaEvent="category-save-next"
                loading={isStepLoading}
                disabled={allAddedKeywords.length === 0}
                onFormsValidationSuccess={async (formData, nextStep, toStep) => {
                  setIsStepLoading(true);

                  let allAddedKeywords = stepData.keywordEntity.getAllAddedKeywords();
                  if (allAddedKeywords.length === 0) {
                    message.error(
                      t(
                        'Please select at least one service and product or add a custom one.'
                      )
                    );
                    setIsStepLoading(false);
                    return;
                  }

                  // validate the form
                  try {
                    await form.validateFields();
                  } catch (error) {
                    console.error('Form validation failed:', error);
                    message.error(
                      t(
                        'Please select at least one service and product or add a custom one.'
                      )
                    );
                    setIsStepLoading(false);
                    return;
                  }

                  try {
                    // Mark keywords provided then go to next step immediately (avoid blank screen during exit animation)
                    stepData.keywordEntity.setKeywordsProvided(true);
                    await CategoryKeywordModel.moveToNextStep();
                  } catch (error) {
                    console.error('Error moving to next step:', error);
                    message.error(
                      t(
                        'An error occurred while moving to the next step. Please try again later.'
                      )
                    );
                  } finally {
                    setIsStepLoading(false);
                  }
                }}
                count={allAddedKeywords.length}
                maxCount={5}
                counterText={`${allAddedKeywords.length}/5 ${t('selected')}`}
              />
            </motion.div>
          </CardContainer>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default KeywordsStep;
